import { Button, DropdownMenu } from '@cloudflare/kumo';
import { SignOut } from '@phosphor-icons/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, Navigate, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useState } from 'react';
import { api, meQuery, queryClient, unwrap } from '../api.ts';
import wirechunkLogo from '../assets/wirechunk-logo.svg';
import { ErrorNotice, Loading } from '../common.tsx';
export function ProtectedLayout() {
  const navigate = useNavigate();
  const me = useQuery(meQuery);
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const signOut = useMutation({
    mutationFn: async () => unwrap(await api.POST('/api/auth/sign-out')),
    onSuccess: async () => {
      queryClient.clear();
      await navigate({ to: '/sign-in' });
    },
  });
  const resend = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST('/api/auth/send-verification-email', {
          body: { email: me.data!.email },
        }),
      ),
  });
  if (me.isPending) return <Loading />;
  if (me.error && 'status' in me.error && me.error.status === 401) return <SignInRequired />;
  if (me.error) return <ErrorNotice error={me.error} />;
  const verified = me.data.emailVerified === true;
  return (
    <>
      <header className="sticky top-0 z-30 flex h-(--app-header-height) items-center justify-between border-b border-b-kumo-line bg-kumo-base px-5 py-0 min-[761px]:px-10">
        <div className="inline-flex shrink-0 items-center">
          <img className="block h-6.5" src={wirechunkLogo} alt="Wirechunk" />
        </div>
        <div className="flex items-center gap-6 text-[13px] text-kumo-subtle">
          {verified && (
            <nav aria-label="Main navigation" className="flex items-center gap-4">
              {headerLinks.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  aria-current={
                    (
                      item.to === '/chats'
                        ? pathname === '/chats' || pathname.startsWith('/chats/')
                        : pathname === '/' || pathname.startsWith('/sites/')
                    )
                      ? 'page'
                      : undefined
                  }
                  className="rounded py-2 font-semibold hover:text-kumo-default aria-[current=page]:text-kumo-default aria-[current=page]:underline aria-[current=page]:underline-offset-8"
                  activeOptions={{ exact: item.to === '/' }}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          )}
          <AccountMenu
            name={me.data.name}
            verified={verified}
            signingOut={signOut.isPending}
            onSignOut={() => signOut.mutate()}
          />
        </div>
      </header>
      <ErrorNotice error={signOut.error} />
      {verified ? (
        <Outlet />
      ) : (
        <main className="mx-auto mt-[clamp(56px,10vh,104px)] mb-12 w-full max-w-[528px] px-6">
          <h1 className="mb-4 text-[28px] leading-[1.2] font-semibold tracking-[-0.025em]">
            Verify your email
          </h1>
          <p className="text-[15px] leading-[1.65] text-kumo-subtle">
            Please click the link we sent to{' '}
            <strong className="font-medium text-kumo-default wrap-anywhere">{me.data.email}</strong>
          </p>
          <div className="grid gap-3 mt-7">
            <ErrorNotice error={resend.error} />
            <div>
              <Button
                className="-mx-2"
                variant="ghost"
                size="sm"
                onClick={() => resend.mutate()}
                disabled={resend.isPending}
              >
                {resend.isPending ? 'Sending…' : 'Resend verification email'}
              </Button>
            </div>
            {resend.isSuccess && (
              <p className="leading-[1.65] text-[14px] text-kumo-subtle" role="status">
                Verification email sent.
              </p>
            )}
          </div>
          {import.meta.env.DEV && (
            <a className="inline-block mt-6" href="/__dev/mail" target="_blank" rel="noreferrer">
              Open development mailbox
            </a>
          )}
        </main>
      )}
    </>
  );
}
const headerLinks = [
  { to: '/', label: 'Sites' },
  { to: '/chats', label: 'Chats' },
] as const;
function firstInitial(name: string) {
  const letter = [...name.trim()][0];
  return letter ? letter.toLocaleUpperCase() : '?';
}
function AccountItems({
  name,
  verified,
  signingOut,
  onSignOut,
}: {
  name: string;
  verified: boolean;
  signingOut: boolean;
  onSignOut: () => void;
}) {
  return (
    <>
      <DropdownMenu.Group>
        <DropdownMenu.Label>{name}</DropdownMenu.Label>
      </DropdownMenu.Group>
      <DropdownMenu.Separator />
      {verified && (
        <>
          <DropdownMenu.LinkItem href="/connections" closeOnClick render={<Link to="/connections" />}>
            Connected agents
          </DropdownMenu.LinkItem>
          <DropdownMenu.LinkItem href="/billing" closeOnClick render={<Link to="/billing" />}>
            Billing
          </DropdownMenu.LinkItem>
        </>
      )}
      <DropdownMenu.Item icon={SignOut} disabled={signingOut} onClick={onSignOut}>
        Sign out
      </DropdownMenu.Item>
    </>
  );
}
function AccountMenu({
  name,
  verified,
  signingOut,
  onSignOut,
}: {
  name: string;
  verified: boolean;
  signingOut: boolean;
  onSignOut: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenu.Trigger
        className="p-0 border-0 border-current w-9 h-9 rounded-full bg-kumo-contrast text-kumo-inverse text-[14px] font-semibold grid place-items-center leading-[1] [&:hover]:bg-kumo-contrast/90 [&[data-popup-open]]:bg-kumo-contrast/90"
        aria-label="Account menu"
      >
        {firstInitial(name)}
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="end">
        <AccountItems name={name} verified={verified} signingOut={signingOut} onSignOut={onSignOut} />
      </DropdownMenu.Content>
    </DropdownMenu>
  );
}
function SignInRequired() {
  // Keep the destination stable while the router transitions away from this layout.
  const [returnTo] = useState(() => `${location.pathname}${location.search}${location.hash}`);
  return <Navigate to="/sign-in" search={{ returnTo }} />;
}
